import {
	getProethicaModuleNumber,
	getProethicaTestByCollectionId,
	getProethicaTestByKind,
} from "@constants/demo/proethica-tests";
import { isProethicaTestPassed } from "@features/report/helpers/proethica-test-report";
import { useSession, useSessionStore } from "@features/session";
import { useProethicaSessionStore } from "@features/session/hooks/useProethicaSessionStore";
import { useProethicaSessionSync } from "@features/session/hooks/useProethicaSessionSync";
import { hasProductAccess } from "@helpers/content/tag";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import useAppConfigIds from "@hooks/useAppConfigIds";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useProductMetadata } from "@hooks/useProduct";
import type { MixedItemTag } from "@memorang/types";
import { useToast } from "@memorang/ui";
import { router } from "expo-router";
import type { TFunction } from "i18next";
import { useCallback, useMemo, useState } from "react";
import type { EXAM_STATUS } from "types/tag";
import {
	type ProethicaExam,
	applyProethicaPremiumLock,
	getProethicaModuleListProps,
	getProethicaModulesGroupedByStatus,
	resolveProethicaCompletedEntryAction,
	resolveProethicaLiveStatus,
	resolveProethicaModuleItems,
} from "../helpers/proethica-module-list";
import { useProethicaTestScores } from "./useProethicaTestScores";

const preTestCollectionId = getProethicaTestByKind("pretest")?.collectionId;
const postTestCollectionId = getProethicaTestByKind("posttest")?.collectionId;

const openEntryReport = (
	moduleId: string,
	numericId: number,
	replace?: boolean,
) => {
	useSessionStore.getState().resetSessionStore();
	useSessionStore.getState().setModuleId(moduleId);
	(replace ? router.replace : router.push)(`/session/${numericId}/report/`);
};

const blockUnresolvableReport = (
	moduleId: string,
	showToast: (options: { message: string }) => void,
) => {
	captureErrorToSentry(
		new Error("Completed proethica entry has no resolvable numericId"),
		{ moduleId },
	);
	showToast({
		message: "Your results aren't available right now. Please try again later.",
	});
};

// Single source of truth for ProEthica module buttons across every surface
// (Modules screen, home list, quick-study dialog). Live per-module status comes
// from the distribution `exams` array; pre/post completion comes from the test
// flags, and `startingModuleId` drives the per-button loader.
export const useProethicaModuleList = () => {
	const storePreTestCompleted = useProethicaSessionStore(
		(state) => state.preTestCompleted,
	);
	const storePostTestPassed = useProethicaSessionStore(
		(state) => state.postTestPassed,
	);
	const { data: mixedItemDist, dataUpdatedAt } = useMixedItemDist();
	useProethicaSessionSync(mixedItemDist?.exams, dataUpdatedAt);
	const { data: productMetadata } = useProductMetadata();
	const { productUuid } = useAppConfigIds();
	const hasAccess = hasProductAccess(productMetadata, productUuid);
	const freeTagIds = mixedItemDist?.freeTagIds ?? [];
	const {
		handleDirectSessionStart,
		handleStartProethicaTest,
		resumeTrackedTestSession,
	} = useSession();
	const { showToast } = useToast();
	const [startingModuleId, setStartingModuleId] = useState<string | null>(null);

	const { examByCollectionId, examStatusByCollectionId } = useMemo(() => {
		const byId = new Map<string, ProethicaExam>();
		const statusById: Record<string, EXAM_STATUS | undefined> = {};
		for (const exam of mixedItemDist?.exams ?? []) {
			const id = String(exam.collectionId);
			byId.set(id, exam);
			statusById[id] = exam.status as EXAM_STATUS | undefined;
		}
		return { examByCollectionId: byId, examStatusByCollectionId: statusById };
	}, [mixedItemDist?.exams]);

	const preTestCompleted =
		storePreTestCompleted ||
		examStatusByCollectionId[String(preTestCollectionId)] === "COMPLETE";

	// The exam row only reports COMPLETE (a failed post-test reads the same), so
	// the pass is derived from the echo-scored session to hold on a fresh
	// device; the locally persisted flag covers the moments right after submit
	// and skips the fetch once set.
	const { testScores: postTestScores } = useProethicaTestScores(
		mixedItemDist?.exams,
		{
			preTestCompleted: storePreTestCompleted,
			postTestPassed: storePostTestPassed,
		},
		{ kind: "posttest", enabled: !storePostTestPassed },
	);
	const postTestScore = postTestScores.get(String(postTestCollectionId));
	const postTestPassed =
		storePostTestPassed ||
		(postTestScore != null && isProethicaTestPassed(postTestScore));

	const liveStatusFor = useCallback(
		(tag: MixedItemTag) =>
			resolveProethicaLiveStatus(tag, {
				examStatusByCollectionId,
				preTestCompleted,
				postTestPassed,
			}),
		[examStatusByCollectionId, preTestCompleted, postTestPassed],
	);

	const getModuleListItemProps = useCallback(
		(tag: MixedItemTag, t: TFunction) => ({
			...getProethicaModuleListProps(tag, t, liveStatusFor(tag), hasAccess),
			hasAccess,
			loading: startingModuleId === String(tag.id),
		}),
		[liveStatusFor, startingModuleId, hasAccess],
	);

	const getModulesGroupedByStatus = useCallback(
		(tags: MixedItemTag[], t: TFunction) =>
			getProethicaModulesGroupedByStatus(tags, t, liveStatusFor, hasAccess),
		[liveStatusFor, hasAccess],
	);

	const resolveModuleItems = useCallback(
		(items: MixedItemTag[], treatAsCompleteTagId?: string) => {
			const completedModuleNumbers = items
				.filter(
					(tag) =>
						liveStatusFor(tag) === "COMPLETE" ||
						String(tag.id) === treatAsCompleteTagId,
				)
				.map(getProethicaModuleNumber)
				.filter((moduleNumber): moduleNumber is number => moduleNumber != null);
			const resolved = resolveProethicaModuleItems(items, {
				preTestCompleted,
				completedModuleNumbers,
			});
			return applyProethicaPremiumLock(resolved, hasAccess, freeTagIds);
		},
		[liveStatusFor, preTestCompleted, hasAccess, freeTagIds],
	);

	// Completed entries (modules and tests) route to their report here instead
	// of re-creating a session; the report's explicit "Retake post-test" button
	// intentionally bypasses this guard by calling handleStartProethicaTest
	// directly. The exam row is the source of truth so this holds on a fresh
	// device with no locally tracked sessions.
	const startModule = useCallback(
		async (moduleId: string, opts?: { replace?: boolean }) => {
			const test = getProethicaTestByCollectionId(moduleId);
			const exam = examByCollectionId.get(moduleId);
			const tracked =
				useProethicaSessionStore.getState().sessionByModule[moduleId];
			const action = resolveProethicaCompletedEntryAction(
				moduleId,
				exam,
				tracked,
				{ preTestCompleted, postTestPassed },
			);
			if (action.kind === "report") {
				openEntryReport(moduleId, action.numericId, opts?.replace);
				return;
			}
			if (action.kind === "blocked") {
				blockUnresolvableReport(moduleId, showToast);
				return;
			}

			setStartingModuleId(moduleId);
			try {
				if (test) {
					// A paused (still ACTIVE) test resumes its tracked session instead
					// of minting a fresh one and losing prior attempts.
					if (await resumeTrackedTestSession(moduleId, opts?.replace)) {
						return;
					}
					await handleStartProethicaTest({
						testId: moduleId,
						replace: opts?.replace,
					});
				} else {
					await handleDirectSessionStart({ moduleId, replace: opts?.replace });
				}
			} finally {
				setStartingModuleId(null);
			}
		},
		[
			examByCollectionId,
			preTestCompleted,
			postTestPassed,
			showToast,
			handleDirectSessionStart,
			handleStartProethicaTest,
			resumeTrackedTestSession,
		],
	);

	return {
		startingModuleId,
		hasAccess,
		liveStatusFor,
		getModuleListItemProps,
		getModulesGroupedByStatus,
		resolveModuleItems,
		startModule,
		preTestCompleted,
		postTestPassed,
	};
};
